"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Input } from "@repo/ui/components/ui/input";
import { Separator } from "@repo/ui/components/ui/separator";
import { useMediaQuery } from "@repo/ui/lib/use-media-query-hook";
import { cn } from "@repo/ui/lib/utils";
import { brandSearchUrl } from "@/algolia/search-url";
import { getAllBrands } from "@/actions/get-all-brands";
import {
  BRAND_ARTICLE,
  BRAND_GRID,
  BrandsHeader,
  BrandsSkeleton,
} from "@/(public)/brand/brands-shell";

const SECTION_KEYS = [
  "0-9",
  ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
];

function bucketKey(brand: string): string {
  const first = brand.trim().charAt(0).toUpperCase();
  return first >= "A" && first <= "Z" ? first : "0-9";
}

export default function Brands({ initialBrands }: { initialBrands: string[] }) {
  const t = useTranslations("Brands");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Nel caso normale i brand arrivano già dal server e non parte nessuna
  // richiesta. La query serve solo come rete di sicurezza quando il render
  // server side non è riuscito a raggiungere Algolia: in quel caso si ritenta
  // attraverso la server action, cioè sulla nostra origin.
  const {
    data: brands = [],
    isFetching,
    isError,
    refetch,
  } = useQuery<string[]>({
    queryKey: ["all-brands"],
    queryFn: () => getAllBrands(),
    initialData: initialBrands.length > 0 ? initialBrands : undefined,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const { groups, activeSections, count } = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const groups: Record<string, string[]> = {};
    let count = 0;
    for (const brand of brands) {
      if (q && !brand.toLowerCase().includes(q)) continue;
      (groups[bucketKey(brand)] ??= []).push(brand);
      count++;
    }
    return {
      groups,
      activeSections: SECTION_KEYS.filter((s) => groups[s]?.length),
      count,
    };
  }, [brands, filter]);

  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  useEffect(() => {
    if (activeSections.length === 0) {
      setActiveLetter(null);
      return;
    }

    const topOffset = isDesktop ? 210 : 150;
    const intersecting = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const letter = entry.target.id.replace("section-", "");
          if (entry.isIntersecting) intersecting.add(letter);
          else intersecting.delete(letter);
        }
        const next = SECTION_KEYS.find((s) => intersecting.has(s));
        if (next !== undefined) setActiveLetter(next);
      },
      {
        rootMargin: `-${topOffset}px 0px -60% 0px`,
        threshold: 0,
      },
    );

    for (const letter of activeSections) {
      const el = document.getElementById(`section-${letter}`);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [activeSections, isDesktop]);

  return (
    <article className={BRAND_ARTICLE}>
      <BrandsHeader title={t("title")} subtitle={t("subtitle")}>
        {/*<div className="mt-8 max-w-md">*/}
        {/*  <Input*/}
        {/*    type="search"*/}
        {/*    placeholder={t("filter-placeholder")}*/}
        {/*    value={filter}*/}
        {/*    onChange={(e) => setFilter(e.target.value)}*/}
        {/*    rounded*/}
        {/*    icon="Search"*/}
        {/*  />*/}
        {/*</div>*/}

        {/*{!isLoading && (*/}
        {/*  <p className="mt-3 text-sm text-secondary-black/60 dark:text-primary-dustyWhite/70">*/}
        {/*    {t("count", { count })}*/}
        {/*  </p>*/}
        {/*)}*/}
      </BrandsHeader>

      {isFetching && brands.length === 0 ? (
        <BrandsSkeleton />
      ) : brands.length === 0 ? (
        <div className="px-10 py-20 flex flex-col items-center gap-4 text-center text-2xl max-md:text-lg text-secondary-black/60 dark:text-primary-dustyWhite/70">
          <p>{isError ? t("error") : t("empty")}</p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-base underline text-primary-magenta hover:opacity-80 disabled:opacity-50"
          >
            {isFetching ? t("loading") : t("retry")}
          </button>
        </div>
      ) : count === 0 ? (
        <div className="px-10 py-20 text-center text-2xl max-md:text-lg text-secondary-black/60 dark:text-primary-dustyWhite/70">
          {t("empty")}
        </div>
      ) : (
        <>
          <nav
            aria-label="A-Z"
            className="sticky top-[88px] md:top-[150px] z-10 bg-primary-white dark:bg-primary-black border-y border-secondary-gray1 dark:border-secondary-black"
          >
            <ul className="flex flex-wrap gap-x-2 gap-y-1 px-10 max-md:px-6 py-3">
              {SECTION_KEYS.map((letter) => {
                const enabled = !!groups[letter]?.length;
                const isActive = enabled && activeLetter === letter;
                return (
                  <li key={letter}>
                    <a
                      href={enabled ? `#section-${letter}` : undefined}
                      aria-disabled={!enabled}
                      aria-current={isActive ? "true" : undefined}
                      className={cn(
                        "inline-flex items-center justify-center min-w-[28px] h-[28px] px-2 rounded-md text-sm font-medium transition-colors",
                        enabled
                          ? isActive
                            ? "text-primary-magenta font-bold"
                            : "text-primary-black dark:text-primary-white hover:bg-secondary-gray2 dark:hover:bg-secondary-black"
                          : "text-secondary-black/30 dark:text-primary-dustyWhite/30 cursor-not-allowed",
                      )}
                    >
                      {letter}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          <section className="px-10 py-12 max-md:px-6 max-md:py-8 flex flex-col gap-12 max-md:gap-8">
            {activeSections.map((letter) => (
              <div
                key={letter}
                id={`section-${letter}`}
                className="scroll-mt-[140px] md:scroll-mt-[210px]"
              >
                <h2 className="text-4xl max-md:text-2xl font-bold text-primary-magenta mb-4">
                  {letter}
                </h2>
                <Separator className="mb-6" />
                <ul className={BRAND_GRID}>
                  {groups[letter]!.map((brand) => (
                    <li key={brand}>
                      <Link
                        href={brandSearchUrl(brand)}
                        className="uppercase block py-1 text-base max-md:text-sm text-primary-black dark:text-primary-white hover:text-primary-magenta hover:underline transition-colors truncate"
                        title={brand}
                      >
                        {brand}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </>
      )}
    </article>
  );
}
